import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as Sentry from "@sentry/react";
import { useAuth } from "@/auth/useAuth";
import type { ChatMessage } from "@/types";
import { getChatSession, getChatSessionCliente, uploadChatFile, startChatFromWizard } from "@/api/chat";
import { qrApi } from "@/api/qr";
import type { WizardPayload } from "@/chat/wizard/WizardConfig";
import { ApiError } from "@/api/client";
import { buildBatchFrames } from "@/chat/sendBatch";
import { comprimiFoto } from "@/lib/comprimiFoto";
import { sessioneBreve } from "./sessione";

interface ChatContextType {
  messages: ChatMessage[];
  sendMessage: (content: string, attachment?: File) => void;
  sendBatch: (files: File[]) => Promise<void>;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isConnected: boolean;
  isLoading: boolean;
  ensureConnected: () => void;
  wizardCompleted: boolean;
  showRestartCta: boolean;
  /** Id delle analisi di bolletta avviate dai caricamenti del wizard (vuoto = funzione
   *  spenta o nessuna bolletta): il riquadro «Spesa per la vendita» nasce da questi. */
  analisiIds: string[];
  /** Cambia a ogni nuova consulenza: invalida le analisi ripristinate dalla sessione. */
  analisiAzzera: number;
  startFromWizard: (payload: WizardPayload, bolletta: File[], documento: File[]) => Promise<void>;
  requestNewWizard: () => void;
  skipWizard: () => void;
  /** Manda un frame di controllo grezzo sulla WS (es. `[batch_inizio n=K]`), senza
   *  passare da sendMessage: non deve comparire come bolla nella lista messaggi. */
  sendRaw: (text: string) => void;
  /** true nella sessione QR (spec §8 "Chat, sessione QR — interfaccia"): i
   *  componenti condivisi (ChatInput, MessageList) lo leggono per usare i testi
   *  al Lei invece di quelli del personale, senza un prop dedicato da passare
   *  a mano in ogni punto di uso. */
  isQr: boolean;
  /** true se l'ultimo allegato mandato in chat durante la sessione QR (fix
   *  round 2, finding 2) non è arrivato al server: QrChat mostra il testo
   *  approvato + «Nuovo tentativo» quando è true. */
  qrAllegatoFallito: boolean;
  /** Rimanda l'allegato QR fallito (stesso file, stesso `kind`). No-op fuori
   *  dalla sessione QR o senza un fallimento in sospeso. */
  retryAllegatoQr: () => void;
}

const ChatCtx = createContext<ChatContextType | null>(null);

const MAX_RECONNECT = 3;
const HEARTBEAT_MS = 5 * 60 * 1000;
const WS_OPEN_WAIT_MS = 5000;

interface SessioneQr {
  session_id: string;
  cat_ws_url: string;
  /** Il token del QR (`/q/:token`): serve a `qrApi.upload` per gli allegati
   *  mandati durante la conversazione (fix round 2, finding 2). */
  token: string;
}

export function ChatProvider({ children, sessioneQr }: { children: ReactNode; sessioneQr?: SessioneQr }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [wizardCompleted, setWizardCompleted] = useState(false);
  const [showRestartCta, setShowRestartCta] = useState(false);
  const [analisiIds, setAnalisiIds] = useState<string[]>([]);
  const [analisiAzzera, setAnalisiAzzera] = useState(0);

  const { hasRole } = useAuth();
  const isCliente = hasRole("cliente");

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCount = useRef(0);
  const ultimaChiusuraRef = useRef("nessuna");
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  // Specchio di `messages` per gli handler WS: leggerlo via ref invece che via
  // closure evita di mettere `messages.length` nelle deps di attachWsHandlers/connect
  // (cambiava a ogni messaggio → connect cambiava → l'effetto riapriva la WS →
  // fino a 3 connessioni concorrenti per un solo avvio wizard).
  const messagesRef = useRef<ChatMessage[]>(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Specchio di `sessioneQr` per connect/attachWsHandlers: letto via ref (non nelle
  // deps) per lo stesso motivo di messagesRef — sessioneQr è un prop stabile nel
  // caso reale (creato una volta da QrChat), ma metterlo comunque nelle deps
  // legherebbe l'identità di connect/attachWsHandlers a un valore esterno senza
  // bisogno (regola CLAUDE.md sul deps-churn).
  const sessioneQrRef = useRef<SessioneQr | undefined>(sessioneQr);
  useEffect(() => { sessioneQrRef.current = sessioneQr; }, [sessioneQr]);

  const cleanup = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Un caricamento fallito (bolletta esclusa, che ha già il suo errore nel wizard)
  // finiva solo su Sentry: l'operatore restava senza sapere che il documento/allegato
  // non era arrivato al server (502 cat_upload_failed su HEIC, 9/9/2026).
  // Il contatore è necessario perché due documenti falliti nello stesso millisecondo
  // (due HEIC in ciclo, 9/9/2026) producevano lo stesso id `m<ts>-upload` → chiavi
  // React duplicate.
  const avvisiCount = useRef(0);
  const avvisaCaricamentoFallito = useCallback((nome: string) => {
    avvisiCount.current += 1;
    // L'id va costruito QUI, non dentro l'updater di setMessages: il corpo
    // dell'updater funzionale gira quando React lo elabora, non alla chiamata,
    // e a quel punto avvisiCount.current potrebbe già riflettere un secondo
    // avviso nel frattempo incrementato — due id identici lo stesso.
    const id = `m${Date.now()}-upload-${avvisiCount.current}`;
    setMessages((prev) => [...prev, {
      id,
      role: "assistant",
      content: `Documento «${nome}» non caricato. Va inviato di nuovo.`,
      timestamp: new Date().toISOString(),
    }]);
  }, []);

  // Sessione QR: un allegato mandato durante la conversazione (non il batch
  // iniziale di QrChat) non ha un cookie di sessione da presentare a
  // `/api/chat/upload` (l'anonimo col token non è loggato) — va per forza da
  // `qrApi.upload`, con `kind` fisso a "bolletta": ogni allegato mandato in
  // chat dopo l'ingresso è una bolletta (di nuovo, o una pagina mancante),
  // mai un documento d'identità (quello ha il suo passo dedicato). In caso di
  // fallimento il file resta qui per «Nuovo tentativo» (testo approvato §8).
  const qrAllegatoFallitoRef = useRef<File | null>(null);
  const [qrAllegatoFallito, setQrAllegatoFallito] = useState(false);

  const tentaUploadQrAllegato = useCallback((file: File) => {
    const qr = sessioneQrRef.current;
    if (!qr) return;
    qrApi.upload(qr.token, qr.session_id, file, "bolletta")
      .then(() => {
        qrAllegatoFallitoRef.current = null;
        setQrAllegatoFallito(false);
        setTimeout(() => {
          wsRef.current?.send(JSON.stringify({ text: "[file_uploaded]" }));
        }, 1500);
      })
      .catch((err) => {
        Sentry.captureException(err, { extra: { sessionId: sessioneBreve(sessionIdRef.current) } });
        qrAllegatoFallitoRef.current = file;
        setQrAllegatoFallito(true);
        setIsLoading(false);
      });
  }, []);

  const retryAllegatoQr = useCallback(() => {
    if (qrAllegatoFallitoRef.current) tentaUploadQrAllegato(qrAllegatoFallitoRef.current);
  }, [tentaUploadQrAllegato]);

  /** Attach onmessage/onclose/onerror/heartbeat to an already-created WebSocket. */
  const attachWsHandlers = useCallback((ws: WebSocket, connectFn: () => void) => {
    ws.onopen = () => {
      // Sessione QR: chiede alla chat i messaggi che non ha (tutti, dopo un
      // ricaricamento) e dice perché era caduta — prima di tutto il resto, così il
      // recupero arriva prima del [batch_inizio] (chat_recupero nel plugin).
      if (sessioneQrRef.current) {
        const giaRicevuti = messagesRef.current.filter((m) => m.role === "assistant").length;
        ws.send(JSON.stringify({
          text: `[riprendi dopo=${giaRicevuti} chiusura=${ultimaChiusuraRef.current}]`,
        }));
      }
      setIsConnected(true);
      reconnectCount.current = 0;
      // Send silent greeting to trigger Cat's welcome if not already sent.
      // Sessione QR: niente saluto — il primo messaggio è [batch_inizio] (QrChat),
      // il benvenuto al *tu* non deve comparire in una sessione al Lei (Task 7).
      if (!sessioneQrRef.current && messagesRef.current.length === 0) {
        ws.send(JSON.stringify({ text: "ciao" }));
      }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Only show final complete message, ignore streaming tokens
        if (data.type !== "chat") return;
        let content: string = data.content ?? "";
        if (!content) return;

        // Parse [wizard_restart] marker
        if (content.includes("[wizard_restart]")) {
          content = content.replace("[wizard_restart]", "").trim();
          setShowRestartCta(true);
        }

        // Marker [in_corso]: il lavoro continua dopo questo messaggio (lettura bolletta,
        // 6-10s di latenza OCR). L'arrivo di un messaggio normalmente spegne i puntini
        // animati e lascia una frase ferma, che si legge come un blocco: qui li si tiene
        // accesi finché non arriva il messaggio successivo.
        let ancoraInCorso = false;
        if (content.includes("[in_corso]")) {
          content = content.replace("[in_corso]", "").trim();
          ancoraInCorso = true;
        }

        // Recupero della sessione QR: i messaggi del cliente tornano marcati [utente].
        const delCliente = Boolean(sessioneQrRef.current) && content.startsWith("[utente]");
        if (delCliente) content = content.slice("[utente]".length);
        const msg: ChatMessage = {
          id: `m${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          role: delCliente ? "user" : "assistant",
          content,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, msg]);
        setIsLoading(ancoraInCorso);
      } catch (err) {
        Sentry.captureException(err, { extra: { sessionId: sessioneBreve(sessionIdRef.current) } });
      }
    };

    ws.onclose = (event) => {
      setIsConnected(false);
      // Motivo della caduta, riferito alla chat alla riconnessione (sessione QR):
      // codice di chiusura e se la pagina era nascosta (schermo spento, altra app).
      ultimaChiusuraRef.current = `${event.code ?? "?"} nascosta=${
        typeof document !== "undefined" && document.visibilityState === "hidden" ? "si" : "no"}`;
      if (event.code !== 1000 && event.code !== 1001 && reconnectCount.current >= MAX_RECONNECT) {
        Sentry.captureMessage("WebSocket closed after max reconnects", {
          level: "warning",
          extra: { sessionId: sessioneBreve(sessionIdRef.current), code: event.code, reason: event.reason },
        });
      }
      if (reconnectCount.current < MAX_RECONNECT) {
        const delay = Math.pow(2, reconnectCount.current) * 1000;
        reconnectCount.current++;
        setTimeout(() => connectFn(), delay);
      }
    };

    ws.onerror = (_event) => {
      // onerror is always followed by onclose — no actionable info here
    };

    // Sessione QR: niente battito. Non c'è login del personale da cui ripartire, e
    // l'anonimo col token non ha un `getChatSession` da interrogare.
    if (sessioneQrRef.current) return;

    heartbeatRef.current = setInterval(async () => {
      try {
        const check = await getChatSession();
        if (check.is_new) {
          cleanup();
          setMessages([]);
          setIsConnected(false);
          // Sessione scaduta sul backend (24h di età, o un suo riavvio): senza
          // riaprire la WS il messaggio dopo finiva su wsRef.current null e restava
          // con lo spinner per sempre. connectFn arriva come parametro: nessuna dep.
          connectFn();
        }
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 401)) {
          Sentry.captureException(err, { extra: { sessionId: sessioneBreve(sessionIdRef.current) } });
        }
      }
    }, HEARTBEAT_MS);
  }, [cleanup]);

  const connect = useCallback(async () => {
    // Don't reconnect if already connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    cleanup();
    try {
      const qr = sessioneQrRef.current;
      // Sessione QR: è la stessa per tutta la visita, quindi una riconnessione dopo una
      // caduta della WS non è una sessione nuova — `is_new: true` svuotava la chat e il
      // cliente perdeva offerte e dati appena arrivati (prova in produzione 2026-09-29).
      const session = qr
        ? { session_id: qr.session_id, cat_ws_url: qr.cat_ws_url, is_new: false }
        : isCliente ? await getChatSessionCliente() : await getChatSession();
      sessionIdRef.current = session.session_id;

      if (session.is_new) {
        setMessages([]);
      }

      const ws = new WebSocket(session.cat_ws_url);
      wsRef.current = ws;

      attachWsHandlers(ws, connect);
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 401)) {
        Sentry.captureException(err, { extra: { sessionId: sessioneBreve(sessionIdRef.current) } });
      }
      setIsConnected(false);
    }
  }, [cleanup, isCliente, attachWsHandlers]);

  const startFromWizard = useCallback(async (payload: WizardPayload, bolletta: File[], documento: File[]) => {
    // 1. wizard-start PRIMA di qualunque upload: resetta history Cat + invia bootstrap.
    //    L'ordine è vincolante, non una preferenza: il bootstrap azzera lo stato
    //    estrazione (bolletta_estratta & co, confine di pratica nuova — incidente
    //    chimera 2026-07-15). Con gli upload prima, l'ingest rabbithole è async e il
    //    bootstrap cancellava le pagine di QUESTA pratica già estratte: pagina persa,
    //    il bot richiedeva POD/PDR/nome/fornitore che erano in bolletta (5 segnalazioni
    //    Giangrossi il 2026-07-28, stessa bolletta, pagina persa ora la 1 ora la 2).
    const session = await startChatFromWizard(payload);
    sessionIdRef.current = session.session_id;

    // Reset messages and reconnect WS with the new session
    setMessages([]);
    setShowRestartCta(false);
    setAnalisiIds([]);
    setAnalisiAzzera((n) => n + 1);
    cleanup();

    const ws = new WebSocket(session.cat_ws_url);
    wsRef.current = ws;
    attachWsHandlers(ws, connect);

    // 2. Bollette DOPO il bootstrap, in sequenza (l'ordine delle pagine conta per il
    //    merge). Il bootstrap ha già fatto il suo reset, quindi nulla le cancella.
    //    [batch_inizio n=K] dichiara quante pagine aspettare: l'ingest resta silente
    //    fino alla K-esima estratta e presenta le offerte una volta sola, sul merge
    //    completo. Nessun [batch_completo]: la chiusura la decide il plugin quando ha
    //    contato le K estrazioni, non il frontend quando ha finito di caricare
    //    (l'ingest è async, il file caricato non è ancora estratto).
    if (bolletta.length) {
      //    L'attesa dell'apertura WS è limitata: [batch_inizio] è un'ottimizzazione,
      //    non una precondizione. Senza, l'ingest mostra le offerte pagina per pagina
      //    invece che una volta sola sul merge: peggio, ma la consulenza parte. Ora
      //    che il passaggio alla chat sta dopo (vedi sotto), un'attesa illimitata qui
      //    inchioderebbe l'operatore sullo spinner del wizard — lo stesso difetto
      //    spostato di una schermata.
      await new Promise<void>((resolve) => {
        if (ws.readyState === 1) return resolve();   // OPEN
        const timer = setTimeout(resolve, WS_OPEN_WAIT_MS);
        const prev = ws.onopen;
        ws.onopen = (ev) => { clearTimeout(timer); prev?.call(ws, ev as Event); resolve(); };
      });
      if (ws.readyState === 1) {
        ws.send(JSON.stringify({ text: `[batch_inizio n=${bolletta.length}]` }));
      }
      try {
        for (const b of bolletta) {
          const { analisiId } = await uploadChatFile(b, "bolletta", { analizza: true });
          if (analisiId) setAnalisiIds((prev) => [...prev, analisiId]);
        }
      } catch (err) {
        // Il caricamento della bolletta era l'unico ramo scoperto: i documenti hanno
        // sempre avuto il loro .catch (sotto), le bollette no. Un fallimento qui non
        // arrivava né a Sentry né all'operatore, e la chat restava su "Sto analizzando
        // la bolletta" senza scadenza — segnalazione 6a9fc48d4a3d6ec0f (2026-09-08):
        // 19 minuti d'attesa su due file mai partiti (al server nessuna POST
        // /api/chat/upload in quel tentativo, solo il wizard-start).
        Sentry.captureException(err, {
          extra: { sessionId: sessioneBreve(session.session_id), kind: "bolletta", count: bolletta.length },
        });
        throw new Error(
          "Bolletta non caricata: il file non è arrivato al server. La consulenza non è " +
          "partita — riprovare con «Avvia consulenza», gli allegati restano.",
        );
      }
    }

    // Il passaggio alla chat solo QUI, a bolletta caricata. Prima stava subito dopo
    // l'apertura della WS: ChatPage smontava il wizard all'istante e l'errore di
    // caricamento finiva su un componente non più a video. L'operatore non lo vedeva
    // e si ritrovava in una chat che prometteva un'analisi mai iniziata.
    setWizardCompleted(true);

    // 3. Documenti DOPO, in background: il vision/parse del PDF è lento
    //    (anche ~10s su scansioni da MB) ma l'intestatario serve solo più
    //    tardi (creazione pratica), NON per mostrare le offerte. Ingerisce
    //    nella sessione già avviata, senza bloccare la UI. Fire-and-forget.
    for (const d of documento) {
      uploadChatFile(d, "documento").catch((e) => {
        Sentry.captureException(e, { extra: { sessionId: sessioneBreve(session.session_id), kind: "documento" } });
        avvisaCaricamentoFallito(d.name);
      });
    }
  }, [cleanup, attachWsHandlers, connect, avvisaCaricamentoFallito]);

  const sendRaw = useCallback((text: string) => {
    wsRef.current?.send(JSON.stringify({ text }));
  }, []);

  const requestNewWizard = useCallback(() => {
    setWizardCompleted(false);
    setShowRestartCta(false);
    setAnalisiIds([]);
    setAnalisiAzzera((n) => n + 1);
  }, []);

  const skipWizard = useCallback(() => {
    setWizardCompleted(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      connect();
    }
  }, [isOpen, connect]);

  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  function sendMessage(content: string, attachment?: File) {
    const userMsg: ChatMessage = {
      id: `m${Date.now()}`,
      role: "user",
      content: content || "",
      timestamp: new Date().toISOString(),
      attachment: attachment
        ? { name: attachment.name, type: attachment.type }
        : undefined,
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    if (attachment && attachment.type.startsWith("image/")) {
      // Images: send as base64 data URI via WebSocket (Groq Vision), alleggerite prima
      // come nel QR (lib/comprimiFoto; HEIC non decodificabile → l'originale).
      comprimiFoto(attachment).then((leggera) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUri = reader.result as string;
          wsRef.current?.send(
            JSON.stringify({ text: content || "", image: dataUri })
          );
        };
        reader.readAsDataURL(leggera);
      });
    } else if (attachment && sessioneQrRef.current) {
      // Sessione QR: niente cookie di sessione per /api/chat/upload, vedi
      // tentaUploadQrAllegato sopra.
      tentaUploadQrAllegato(attachment);
    } else if (attachment) {
      // PDFs and other files: upload via rabbithole, then send a follow-up
      // WS message to trigger fast_reply (the hook sets _documento_ricevuto flag)
      uploadChatFile(attachment)
        .then(() => {
          // Small delay to let the hook finish processing
          setTimeout(() => {
            wsRef.current?.send(JSON.stringify({ text: "[file_uploaded]" }));
          }, 1500);
        })
        .catch((err) => {
          Sentry.captureException(err, { extra: { sessionId: sessioneBreve(sessionIdRef.current) } });
          avvisaCaricamentoFallito(attachment.name);
          setIsLoading(false);
        });
    } else {
      wsRef.current?.send(JSON.stringify({ text: content }));
    }
  }

  const sendBatch = useCallback(async (files: File[]) => {
    const capped = files.slice(0, 10);

    const userMsg: ChatMessage =
      capped.length === 1
        ? {
            id: `m${Date.now()}`,
            role: "user",
            content: "",
            timestamp: new Date().toISOString(),
            attachment: { name: capped[0].name, type: capped[0].type },
          }
        : {
            id: `m${Date.now()}`,
            role: "user",
            content: `Carico ${capped.length} file: ${capped.map((f) => f.name).join(", ")}`,
            timestamp: new Date().toISOString(),
          };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      await buildBatchFrames(capped, {
        sendText: (t) => wsRef.current?.send(JSON.stringify({ text: t })),
        sendImage: async (f) => {
          const leggera = await comprimiFoto(f);
          await new Promise<void>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              wsRef.current?.send(JSON.stringify({ text: "", image: reader.result as string }));
              resolve();
            };
            reader.onerror = () => resolve();
            reader.readAsDataURL(leggera);
          });
        },
        uploadPdf: async (f) => {
          await uploadChatFile(f);
        },
      });
    } catch (err) {
      Sentry.captureException(err, { extra: { sessionId: sessioneBreve(sessionIdRef.current) } });
      setIsLoading(false);
    }
  }, []);

  return (
    <ChatCtx.Provider
      value={{
        messages,
        sendMessage,
        sendBatch,
        isOpen,
        setIsOpen,
        isConnected,
        isLoading,
        ensureConnected: connect,
        wizardCompleted,
        showRestartCta,
        analisiIds,
        analisiAzzera,
        startFromWizard,
        requestNewWizard,
        skipWizard,
        sendRaw,
        isQr: Boolean(sessioneQr),
        qrAllegatoFallito,
        retryAllegatoQr,
      }}
    >
      {children}
    </ChatCtx.Provider>
  );
}

export function useChat() {
  const ctx = useContext(ChatCtx);
  if (!ctx) throw new Error("useChat must be used within ChatProvider");
  return ctx;
}
